import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, CheckCircle, XCircle, MessageSquare, Globe } from 'lucide-react';
import { agencyService, AgencyBrandRequest, AgencyBrandRequestStatusCounts } from '@/services/agency.service';
import { toast } from 'react-hot-toast';

export function AgencyBrandRequests() {
  const [requests, setRequests] = useState<AgencyBrandRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const [pageSize] = useState(20);
  const [statusCounts, setStatusCounts] = useState<AgencyBrandRequestStatusCounts>({
    pending: 0,
    approved: 0,
    rejected: 0,
    total: 0,
  });

  useEffect(() => {
    fetchRequests(1, statusFilter);
    fetchStatusCounts();
  }, [statusFilter]);

  const fetchStatusCounts = async () => {
    try {
      const counts = await agencyService.getAgencyBrandRequestStatusCounts();
      setStatusCounts(counts);
    } catch (error) {
      console.error('Failed to fetch status counts:', error);
    }
  };

  // Handle status filter change
  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setCurrentPage(1); // Reset to first page when changing filter
    fetchRequests(1, newStatus);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchRequests(page, statusFilter);
  };

  const fetchRequests = async (page: number = 1, status: string = 'ALL') => {
    try {
      setLoading(true);
      const response = await agencyService.getAgencyBrandRequests(page, pageSize, status);
      setRequests(response.requests);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
      setTotalRequests(response.pagination.total);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'APPROVED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'APPROVED':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Approved</Badge>;
      case 'REJECTED':
        return <Badge variant="secondary" className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Brand Requests Yet</h3>
          <p className="text-muted-foreground mb-4">
            You haven't sent any requests to become a brand partner yet.
          </p>
          <p className="text-sm text-muted-foreground">
            Start by requesting to represent brands that align with your network and expertise.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Filter requests based on status filter
  const filteredRequests = requests.filter(request => 
    statusFilter === 'ALL' || request.status === statusFilter
  );

  if (filteredRequests.length === 0 && statusFilter !== 'ALL') {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No {statusFilter.toLowerCase()} Requests</h3>
          <p className="text-muted-foreground">
            You don't have any {statusFilter.toLowerCase()} brand requests at the moment.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStatusFilter('ALL')}
            className="mt-4"
          >
            View All Requests
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground">Your Brand Requests</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchRequests(currentPage, statusFilter)}
          className="text-sm"
        >
          Refresh
        </Button>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-yellow-50 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-yellow-600">
            {statusCounts.pending}
          </div>
          <div className="text-sm text-yellow-700">Pending</div>
        </div>
        <div className="bg-green-50 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-600">
            {statusCounts.approved}
          </div>
          <div className="text-sm text-green-700">Approved</div>
        </div>
        <div className="bg-red-50 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-red-600">
            {statusCounts.rejected}
          </div>
          <div className="text-sm text-red-700">Rejected</div>
        </div>
      </div>

      {/* Status Filter */}
      <div className="flex gap-2 mb-4">
        <Button
          variant={statusFilter === 'ALL' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleStatusFilterChange('ALL')}
        >
          All ({totalRequests})
        </Button>
        <Button
          variant={statusFilter === 'PENDING' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleStatusFilterChange('PENDING')}
        >
          Pending ({statusCounts.pending})
        </Button>
        <Button
          variant={statusFilter === 'APPROVED' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleStatusFilterChange('APPROVED')}
        >
          Approved ({statusCounts.approved})
        </Button>
        <Button
          variant={statusFilter === 'REJECTED' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleStatusFilterChange('REJECTED')}
        >
          Rejected ({statusCounts.rejected})
        </Button>
      </div>

      {filteredRequests.map((request) => (
        <Card key={request.id} className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {getStatusIcon(request.status)}
                  <CardTitle className="text-lg">{request.brandDetail.companyName}</CardTitle>
                  {getStatusBadge(request.status)}
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>Requested {formatDate(request.createdAt)}</span>
                  {request.updatedAt !== request.createdAt && (
                    <span>Updated {formatDate(request.updatedAt)}</span>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {/* Brand Details */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {request.brandDetail.website && (
                  <a
                    href={request.brandDetail.website.startsWith('http') ? request.brandDetail.website : `https://${request.brandDetail.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-brand-600 hover:text-brand-700"
                  >
                    <Globe className="w-3 h-3" />
                    {request.brandDetail.website}
                  </a>
                )}
              </div>

              {/* Niches */}
              {request.brandDetail.niches.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {request.brandDetail.niches.map((niche, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {niche.name}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Agency Message */}
              {request.message && (
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-sm font-medium text-muted-foreground mb-1">Your Message:</p>
                  <p className="text-sm text-muted-foreground">{request.message}</p>
                </div>
              )}

              {/* Brand Response */}
              {request.responseMessage && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-blue-700 mb-1">
                    Response from {request.brandDetail.companyName}:
                  </p>
                  <p className="text-sm text-blue-600">{request.responseMessage}</p>
                </div>
              )}

              {/* Status-specific actions */}
              {request.status === 'APPROVED' && (
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-sm text-green-700 font-medium">
                    🎉 Congratulations! You're now an agent for {request.brandDetail.companyName}
                  </p>
                </div>
              )}

              {request.status === 'REJECTED' && (
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-sm text-red-700 font-medium mb-2">
                    Your request was not approved at this time.
                  </p>
                  {request.responseMessage && (
                    <p className="text-sm text-red-600">
                      <strong>Reason:</strong> {request.responseMessage}
                    </p>
                  )}
                  <p className="text-xs text-red-500 mt-2">
                    You can still view this request in your history for reference.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="flex space-x-2">
            <Button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </Button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                onClick={() => handlePageChange(page)}
                variant={currentPage === page ? 'default' : 'outline'}
                size="sm"
              >
                {page}
              </Button>
            ))}
            
            <Button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              variant="outline"
              size="sm"
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Page Info */}
      <div className="text-center text-sm text-muted-foreground mt-4">
        Page {currentPage} of {totalPages} • Showing {requests.length} of {totalRequests} requests
      </div>
    </div>
  );
}
